import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, Tag, X, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { API } from "@/lib/api";

export default function AdminCoupons() {
  const { token } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_type: 'percent', discount_value: '', min_order_amount: '',
    max_uses: '', active: true, applicable_products: [], applicable_categories: []
  });

  // Product/category search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);

  const loadCoupons = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCoupons(res.data))
      .catch(() => toast.error('Chyba načtení kupónů'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadCoupons(); }, [loadCoupons]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const searchProducts = useCallback(async (q) => {
    if (q.length < 2) { setProductResults([]); return; }
    setSearchingProducts(true);
    try {
      const res = await axios.get(`${API}/products?search=${encodeURIComponent(q)}&limit=10`);
      setProductResults(res.data.products || []);
    } catch { setProductResults([]); }
    finally { setSearchingProducts(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchProducts(productSearch), 300);
    return () => clearTimeout(t);
  }, [productSearch, searchProducts]);

  const createCoupon = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error('Zadejte kód kupónu'); return; }
    if (!form.discount_value || parseFloat(form.discount_value) <= 0) { toast.error('Zadejte hodnotu slevy'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        active: form.active,
        applicable_products: form.applicable_products,
        applicable_categories: form.applicable_categories,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kupón vytvořen');
      setShowForm(false);
      setForm({ code: '', discount_type: 'percent', discount_value: '', min_order_amount: '', max_uses: '', active: true, applicable_products: [], applicable_categories: [] });
      loadCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chyba vytváření');
    } finally { setSaving(false); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Opravdu smazat kupón?')) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kupón smazán');
      loadCoupons();
    } catch { toast.error('Chyba mazání'); }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/admin/coupons/${coupon.id}`, { ...coupon, active: !coupon.active }, { headers: { Authorization: `Bearer ${token}` } });
      loadCoupons();
    } catch { toast.error('Chyba aktualizace'); }
  };

  const addProduct = (product) => {
    if (!form.applicable_products.includes(product.id_product)) {
      setForm(f => ({ ...f, applicable_products: [...f.applicable_products, product.id_product] }));
    }
    setProductSearch('');
    setProductResults([]);
  };

  const removeProduct = (id) => {
    setForm(f => ({ ...f, applicable_products: f.applicable_products.filter(p => p !== id) }));
  };

  const toggleCategory = (catId) => {
    setForm(f => ({
      ...f,
      applicable_categories: f.applicable_categories.includes(catId)
        ? f.applicable_categories.filter(c => c !== catId)
        : [...f.applicable_categories, catId]
    }));
  };

  const flatCategories = (cats, depth = 0) => {
    let flat = [];
    for (const cat of cats) {
      flat.push({ ...cat, depth });
      if (cat.children?.length) flat.push(...flatCategories(cat.children, depth + 1));
    }
    return flat;
  };

  return (
    <AdminLayout active="coupons">
      <div className="flex items-center justify-between mb-6" data-testid="admin-coupons">
        <h1 className="text-2xl font-bold text-slate-800">Slevové kupóny</h1>
        <button onClick={() => setShowForm(!showForm)} className="h-10 px-5 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] transition-colors" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4" /> Nový kupón
        </button>
      </div>

      {showForm && (
        <form onSubmit={createCoupon} className="bg-white border border-slate-200 rounded-lg p-6 mb-6" data-testid="coupon-form">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Kód kupónu</label>
              <input type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-full h-11 border border-slate-300 rounded px-4 uppercase" placeholder="SLEVA20" data-testid="coupon-code-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Typ slevy</label>
              <select value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })} className="w-full h-11 border border-slate-300 rounded px-4" data-testid="coupon-type-select">
                <option value="percent">Procentuální (%)</option>
                <option value="fixed">Pevná částka (Kč)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Hodnota slevy</label>
              <input type="number" step="0.01" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} className="w-full h-11 border border-slate-300 rounded px-4" placeholder={form.discount_type === 'percent' ? '10' : '100'} data-testid="coupon-value-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Min. hodnota objednávky (Kč)</label>
              <input type="number" step="0.01" value={form.min_order_amount} onChange={e => setForm({ ...form, min_order_amount: e.target.value })} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="0" data-testid="coupon-min-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Max. použití</label>
              <input type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} className="w-full h-11 border border-slate-300 rounded px-4" placeholder="Bez limitu" data-testid="coupon-maxuses-input" />
            </div>
          </div>

          {/* Product restrictions */}
          <div className="mb-4 border-t border-slate-200 pt-4">
            <label className="text-xs font-bold uppercase text-slate-500 mb-2 block">Omezení na produkty (volitelné)</label>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
              <input
                type="text" value={productSearch} onChange={e => setProductSearch(e.target.value)}
                className="w-full h-11 border border-slate-300 rounded pl-10 pr-4" placeholder="Hledat produkt..."
                data-testid="coupon-product-search"
              />
              {productResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {productResults.map(p => (
                    <button key={p.id_product} type="button" onClick={() => addProduct(p)} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      <span className="font-medium">{p.name}</span> <span className="text-slate-400">#{p.id_product}</span>
                    </button>
                  ))}
                </div>
              )}
              {searchingProducts && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3.5 text-slate-400" />}
            </div>
            {form.applicable_products.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="selected-products">
                {form.applicable_products.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                    #{id} <button type="button" onClick={() => removeProduct(id)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Category restrictions */}
          <div className="mb-4 border-t border-slate-200 pt-4">
            <label className="text-xs font-bold uppercase text-slate-500 mb-2 block">Omezení na kategorie (volitelné)</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto" data-testid="coupon-categories">
              {flatCategories(categories).map(cat => (
                <label key={cat.id_category} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded cursor-pointer text-sm" style={{ paddingLeft: `${cat.depth * 16 + 12}px` }}>
                  <input
                    type="checkbox"
                    checked={form.applicable_categories.includes(cat.id_category)}
                    onChange={() => toggleCategory(cat.id_category)}
                    className="rounded border-slate-300"
                  />
                  {cat.name}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button type="submit" disabled={saving} className="h-10 px-5 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50 transition-colors" data-testid="save-coupon-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Vytvořit kupón
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="h-10 px-5 border border-slate-300 rounded text-sm hover:bg-slate-50">Zrušit</button>
          </div>
        </form>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div> : coupons.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Žádné kupóny</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Kód</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Sleva</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase text-slate-500">Min.</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Použití</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Omezení</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Stav</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase text-slate-500">Akce</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`coupon-${c.code}`}>
                  <td className="px-4 py-3 font-medium font-mono">{c.code}</td>
                  <td className="px-4 py-3">{c.discount_type === 'percent' ? `${c.discount_value}%` : `${c.discount_value} Kč`}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{c.min_order_amount > 0 ? `${c.min_order_amount} Kč` : '-'}</td>
                  <td className="px-4 py-3 text-center text-sm">{c.used_count}{c.max_uses ? `/${c.max_uses}` : ''}</td>
                  <td className="px-4 py-3 text-center text-xs">
                    {(c.applicable_products?.length > 0 || c.applicable_categories?.length > 0) ? (
                      <span className="text-amber-600">
                        {c.applicable_products?.length > 0 && `${c.applicable_products.length} prod.`}
                        {c.applicable_products?.length > 0 && c.applicable_categories?.length > 0 && ' + '}
                        {c.applicable_categories?.length > 0 && `${c.applicable_categories.length} kat.`}
                      </span>
                    ) : <span className="text-slate-400">Vše</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleActive(c)} className={`px-2 py-1 text-xs font-medium rounded ${c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid={`toggle-coupon-${c.code}`}>
                      {c.active ? 'Aktivní' : 'Neaktivní'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => deleteCoupon(c.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-coupon-${c.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
